/**
 * Global ErrorBoundary — Sprint 106 Final Stability.
 *
 * Catches any uncaught render error and shows a professional error
 * screen with cause / solution / suggested-fix + a "Copy request id"
 * button. Never surfaces raw stack traces to end users.
 */
import React from "react";
import { AlertTriangle, RefreshCw, Copy, Home } from "lucide-react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, requestId: null };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      requestId: `ui-${Math.random().toString(36).slice(2, 10)}`,
    };
  }

  componentDidCatch(error, info) {
    // Send to console only — no telemetry endpoint yet.
    console.error("[ErrorBoundary]", error, info);
  }

  copyRid = () => {
    try { navigator.clipboard.writeText(this.state.requestId); } catch (e) { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const err = this.state.error;
    const name = err?.name || "Error";
    const msg  = err?.message || "Unexpected UI error.";
    const solution = name === "TypeError"
      ? "Data may be malformed — try reloading the page."
      : "Retry the operation, or return to the dashboard.";
    return (
      <div style={{
        position: "fixed", inset: 0, background: "#0b0e13", color: "#d7dde5",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24, zIndex: 9999, fontFamily: "Inter, system-ui, sans-serif",
      }} data-testid="enterprise-error-boundary">
        <div style={{
          maxWidth: 560, background: "#0e1218",
          border: "1px solid rgba(244,68,68,0.35)", borderRadius: 12, padding: 24,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <AlertTriangle size={22} color="#f87171" />
            <h1 style={{ fontSize: 18, margin: 0, color: "#f87171" }}>Something went wrong</h1>
          </div>
          <div style={{ fontSize: 12, color: "#8a94a3", letterSpacing: "0.1em",
                        textTransform: "uppercase", marginBottom: 4 }}>Cause</div>
          <div style={{ fontSize: 13, marginBottom: 10 }}>{msg}</div>

          <div style={{ fontSize: 12, color: "#8a94a3", letterSpacing: "0.1em",
                        textTransform: "uppercase", marginBottom: 4 }}>Solution</div>
          <div style={{ fontSize: 13, marginBottom: 10 }}>{solution}</div>

          <div style={{ fontSize: 12, color: "#8a94a3", letterSpacing: "0.1em",
                        textTransform: "uppercase", marginBottom: 4 }}>Reference</div>
          <div style={{
            fontFamily: "JetBrains Mono, monospace", background: "#0b0e13",
            border: "1px solid #1e2531", padding: "6px 10px", borderRadius: 6,
            fontSize: 11, display: "flex", justifyContent: "space-between",
            alignItems: "center", marginBottom: 16,
          }}>
            {this.state.requestId}
            <button onClick={this.copyRid} data-testid="err-copy-rid"
                    style={{ background: "transparent", border: "none",
                             color: "#f4b942", cursor: "pointer" }}>
              <Copy size={14} />
            </button>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => window.location.reload()}
              data-testid="err-reload"
              style={{
                flex: 1, padding: "10px 16px", background: "#f4b942",
                color: "#1a1204", border: "none", borderRadius: 6,
                fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
                textTransform: "uppercase", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              <RefreshCw size={14} /> Reload
            </button>
            <button
              onClick={() => { window.location.href = "/dashboard"; }}
              data-testid="err-home"
              style={{
                flex: 1, padding: "10px 16px", background: "transparent",
                color: "#d7dde5", border: "1px solid #1e2531", borderRadius: 6,
                fontSize: 11, fontWeight: 600, letterSpacing: "0.1em",
                textTransform: "uppercase", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              <Home size={14} /> Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
